import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AiRecommendationKind, CustomOptionInterface } from 'types';
import { generateSelectOptions } from 'utils/dataFormatter';
import { getErrorMessage } from 'utils/v2/errorHelper';

export const aiRecommendationKeys = {
    all: ['aiRecommendation'] as const,
    search: (kind: AiRecommendationKind, country: string, description: string) =>
        ['aiRecommendation', kind, country, description] as const,
};

export interface AiRecommendationRequest {
    kind: AiRecommendationKind;
    countryCode: string;
    description: string;
}

async function fetchAiRecommendations(
    request: AiRecommendationRequest
): Promise<CustomOptionInterface<string | number>[]> {
    const description = request.description.trim();
    if (!description || !request.countryCode) {
        return [];
    }

    if (request.kind === 'occupation') {
        const response = await api.job_recommendation.getRecommendedJobs(
            request.countryCode,
            description
        );
        return generateSelectOptions(response.data?.occupation_options ?? [], 'name', 'id');
    }

    const response = await api.fieldsOfStudy_recommendation.getRecommendedFieldsOfStudy(
        request.countryCode,
        description
    );
    return generateSelectOptions(response.data?.fields_of_study ?? [], 'name', 'id');
}

/** On-demand occupation or field-of-study suggestions. Repeats are cached by prompt. */
export function useAiRecommendations() {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const onError = useCallback(
        (error: unknown) => {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        },
        [dispatch, translate]
    );

    return useMutation({
        mutationFn: (request: AiRecommendationRequest) =>
            queryClient.fetchQuery({
                queryKey: aiRecommendationKeys.search(
                    request.kind,
                    request.countryCode,
                    request.description.trim()
                ),
                queryFn: () => fetchAiRecommendations(request),
                staleTime: 5 * 60 * 1000,
                retry: false,
            }),
        onError,
    });
}
